import { useContext, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import AuthContext from '../../context/authContext';
import useFeedback from '../../hooks/useFeedback';
import { ISSUE_PRIORITIES } from '../../util/issueConstants';

// Forwards a developer-feedback entry to the IT ticket system: the manager
// picks the ticket priority (P1-P5), reviews title and branch, and the
// feedback is closed in the same step with a reply telling the author that an
// IT ticket was opened for them.

// Extracts the first name from a full display name string.
const firstNameOf = fullName => {
  const trimmed = (fullName || '').trim();
  return trimmed ? trimmed.split(/\s+/)[0] : '';
};

// Prefilled ticket title, mirroring the backend derivation.
const buildDefaultTitle = item => {
  const firstLine = String(item?.text || '')
    .split('\n')
    .map(line => line.trim())
    .find(Boolean);
  if (firstLine) return firstLine.length > 120 ? `${firstLine.slice(0, 117)}…` : firstLine;
  return item?.viewKey ? `Feedback zu ${item.viewKey}` : 'Weitergeleitetes Feedback';
};

// Prefilled closing reply telling the author their topic went to the IT.
const buildForwardResponse = (item, responderName) => {
  const recipient =
    firstNameOf(item?.user?.name) || firstNameOf(item?.user?.username) || 'zusammen';
  const responder = firstNameOf(responderName);
  const lines = [
    `Hi ${recipient},`,
    '',
    'danke für dein Feedback! Das ist ein Thema für die IT — ich habe dafür ein IT-Ticket für dich eröffnet. Die IT kümmert sich darum.',
    '',
    'LG',
  ];
  if (responder) lines.push(responder);
  return lines.join('\n');
};

export default function ForwardFeedbackToIssueDialog({ item, onClose, onForwarded }) {
  const authCtx = useContext(AuthContext);
  const { forwardToIssue } = useFeedback();

  const [priority, setPriority] = useState('p3');
  const [branchId, setBranchId] = useState('');
  const [saving, setSaving] = useState(false);

  // Reset the selects whenever another entry is opened, so a previous choice
  // never leaks into the next forwarding.
  const [lastItemId, setLastItemId] = useState(null);
  if (item && String(item._id) !== lastItemId) {
    setLastItemId(String(item._id));
    setPriority('p3');
    setBranchId('');
  }

  // Heavy text inputs stay uncontrolled; the `key` on the fields resets them
  // per entry so the prefill matches the currently opened feedback.
  const titleRef = useRef(null);
  const responseRef = useRef(null);

  const authorBranches = useMemo(
    () => (item?.user?.assignedBranches || []).filter(b => b?.branchId && b?.name),
    [item]
  );

  const submit = async () => {
    if (!item) return;
    const title = (titleRef.current?.value || '').trim();
    const response = (responseRef.current?.value || '').trim();
    if (!response) {
      toast.error('Bitte eine Antwort an die Autorin / den Autor eingeben');
      responseRef.current?.focus();
      return;
    }
    setSaving(true);
    try {
      const result = await forwardToIssue({
        feedbackId: item._id,
        priority,
        branchId: branchId || authorBranches[0]?.branchId || undefined,
        title: title || undefined,
        response,
      });
      toast.success('IT-Ticket eröffnet und Feedback abgeschlossen');
      onForwarded?.(result?.feedback);
      onClose();
    } catch (err) {
      // The hook already shows a German error toast.
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={Boolean(item)} onClose={onClose} fullWidth maxWidth='sm'>
      <DialogTitle>
        An die IT weiterleiten
        <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
          Aus dem Feedback wird ein IT-Ticket erstellt; das Feedback wird gleichzeitig mit der
          Antwort unten abgeschlossen.
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {item && (
          <Stack spacing={2} sx={{ mt: 0.5 }} key={String(item._id)}>
            <Box
              sx={{
                whiteSpace: 'pre-wrap',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'action.hover',
                p: 1.5,
                fontSize: 14,
              }}
            >
              {item.text?.trim() || 'Kein Text hinterlegt.'}
            </Box>

            <TextField
              label='Ticket-Titel'
              inputRef={titleRef}
              defaultValue={buildDefaultTitle(item)}
              fullWidth
              size='small'
              inputProps={{ maxLength: 200 }}
            />

            <FormControl size='small' fullWidth>
              <InputLabel id='forward-issue-priority-label'>Priorität</InputLabel>
              <Select
                labelId='forward-issue-priority-label'
                label='Priorität'
                value={priority}
                onChange={e => setPriority(e.target.value)}
              >
                {ISSUE_PRIORITIES.map(p => (
                  <MenuItem key={p.key} value={p.key}>
                    {p.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>P1 löst sofort die IT-Eskalationskette aus.</FormHelperText>
            </FormControl>

            {authorBranches.length > 0 ? (
              <FormControl size='small' fullWidth>
                <InputLabel id='forward-issue-branch-label'>Standort</InputLabel>
                <Select
                  labelId='forward-issue-branch-label'
                  label='Standort'
                  value={branchId || authorBranches[0]?.branchId || ''}
                  onChange={e => setBranchId(e.target.value)}
                >
                  {authorBranches.map(b => (
                    <MenuItem key={b.branchId} value={String(b.branchId)}>
                      {b.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Typography variant='caption' color='text.secondary'>
                Der Standort des Tickets wird automatisch ermittelt.
              </Typography>
            )}

            <TextField
              label='Antwort an die Autorin / den Autor'
              inputRef={responseRef}
              defaultValue={buildForwardResponse(item, authCtx.currentUser?.userName)}
              multiline
              minRows={6}
              maxRows={12}
              fullWidth
            />
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Abbrechen
        </Button>
        <Button
          variant='contained'
          onClick={submit}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          Ticket eröffnen &amp; Feedback abschließen
        </Button>
      </DialogActions>
    </Dialog>
  );
}
